import React from 'react';
import ringArt from '../../assets/img/portal-ring.png';
import { RING } from '../wall/wallTokens';
import { GLYPHS, MODS, SEV, FAMILIES, familyOf, typeOf, faceTint } from './notificationTypes';

/**
 * One notification icon: frame (the family) + glyph (the subject) + badge (what happened).
 * Handoff: design_handoff_notifications README §"Icon system"; the gallery in
 * `Notification Icons.dc.html` is the reference for every type.
 *
 *   type    a `NotificationType` enum name, OR a family key (the aside draws the family alone)
 *   family  the API's family for an unknown type
 *   size    tile size in px (52 on the page, 44 in the dropdown, 36 / 30 in the aside)
 *   face    { img, initial, tint } — a person's photo or initial takes the tile / the ring's face
 *   live    PORTALS only: true spins the ring (a live, unread Portal); false dims it to .45
 *           (closed); undefined leaves it still at full strength
 *   badge   false hides the mod (the family list never shows one)
 */
export default function NotificationIcon({ type, family = null, size = 48, face = null, live, badge = true, className = '' }) {
  const t = typeOf(type, family);
  const f = familyOf(t.family) || FAMILIES[0];
  const glyphSize = Math.round(size * 0.46);
  const radius = Math.round(size * 0.3);
  const mod = t.mod && badge !== false ? t.mod : null;
  const modSize = Math.max(16, Math.round(size * 0.4));
  const sev = SEV[t.sev] || SEV.neu;

  const strokeProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'square', strokeLinejoin: 'miter' };
  const paths = (d) => d.split(' M').map((p, i) => <path key={i} d={(i ? 'M' : '') + p} />);
  const glyph = (
    <svg key="g" width={glyphSize} height={glyphSize} viewBox="0 0 24 24" {...strokeProps} aria-hidden>
      {paths(GLYPHS[t.glyph] || GLYPHS.bracket)}
    </svg>
  );

  const faceStyle = face ? { background: face.tint || faceTint(face.initial), color: '#000', fontSize: Math.round(size * 0.34), fontWeight: 800 } : null;
  const faceKid = face ? (face.img
    ? <img src={face.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    : (face.initial || '?').slice(0, 1).toUpperCase()) : null;

  const base = { position: 'relative', flexShrink: 0, width: size, height: size, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const centred = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

  let outer = base;
  let kids;
  if (f.frame === 'ring') {
    // The Portal ring around a face (or a black disc with the cyan glyph). It spins only
    // while the Portal is live and unread; a closed one keeps the ring at .45.
    kids = [
      <span
        key="r"
        className={live ? 'pr-portal-ring' : ''}
        style={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animationDuration: live ? '6s' : undefined, opacity: live === false ? 0.45 : 1 }}
        aria-hidden
      />,
      <span
        key="f"
        style={{ position: 'absolute', inset: `${(size * 0.055).toFixed(2)}px`, borderRadius: '50%', overflow: 'hidden', ...centred, ...(face ? faceStyle : { background: '#000', color: f.fg }) }}
      >
        {face ? faceKid : glyph}
      </span>,
    ];
  } else if (face) {
    kids = [
      <span key="f" style={{ position: 'absolute', inset: 0, borderRadius: radius, overflow: 'hidden', ...centred, ...faceStyle }}>
        {faceKid}
      </span>,
    ];
  } else if (f.frame === 'host') {
    outer = { ...base, borderRadius: radius, border: '2px solid transparent', backgroundImage: `linear-gradient(#000,#000), ${RING}`, backgroundOrigin: 'padding-box, border-box', backgroundClip: 'padding-box, border-box', color: '#fff' };
    kids = [glyph];
  } else {
    outer = { ...base, borderRadius: radius, background: f.bg, color: f.fg };
    kids = [glyph];
  }

  return (
    <span aria-hidden className={className} style={outer} data-testid="notification-icon" data-family={f.key} data-frame={face ? 'face' : f.frame || 'tile'}>
      {kids}
      {mod && (
        <span
          data-testid="notification-badge"
          style={{ position: 'absolute', right: -Math.round(modSize * 0.2), bottom: -Math.round(modSize * 0.2), width: modSize, height: modSize, boxSizing: 'border-box', borderRadius: 999, border: '2px solid #fff', background: sev.bg, color: sev.fg, ...centred }}
        >
          <svg width={modSize - 7} height={modSize - 7} viewBox="0 0 16 16" {...strokeProps} strokeWidth={2.6}>
            {paths(MODS[mod])}
          </svg>
        </span>
      )}
    </span>
  );
}

/** The EVERYTHING row's icon in the aside: a grey tile with an asterisk. */
export function EverythingIcon({ size = 36 }) {
  return (
    <span aria-hidden style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: '#F2F2F2', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.36), fontWeight: 800, flexShrink: 0 }}>
      *
    </span>
  );
}
